import { apiUrl } from "../utils/api";
import type { ContactCommRow } from "../components/mail/mailContactAssign";

/** Slim-Meta eines Kontakts für den Zuweisen-Flow (F1b). */
export type ContactCommMeta = {
    /** ContactType.type ('Firma' | 'Person' | …) — steuert die privat/geschäftlich-Wahl. */
    contactType: string | null;
    communications: ContactCommRow[];
    /** Autoritatives Schreibrecht (GET /contact/:id, spiegelt assertContactWritable). */
    canWrite: boolean;
};

/** Der Body von `POST /contact/:id/communication`. */
export type KommunikationPayload = {
    type: string;
    value: string;
    label?: string;
    kind?: string;
    companyId?: number;
    isDefault: boolean;
};

export const contactCommunicationService = {
    /**
     * GET /contact/:id?include=communications → Typ, Kommunikationswege,
     * Schreibrecht. `?include=` erzwingt die Slim-Base (type + communications);
     * `canWrite` hängt der Controller immer an.
     */
    async loadMeta(contactId: number): Promise<ContactCommMeta> {
        const res = await fetch(apiUrl(`contact/${contactId}`, { include: "communications" }));
        if (!res.ok) throw new Error(`Kontaktdaten laden fehlgeschlagen: ${res.status}`);
        const c: any = await res.json();
        return {
            contactType: c?.type?.type ?? null,
            communications: Array.isArray(c?.communications) ? c.communications : [],
            canWrite: !!c?.canWrite,
        };
    },

    /**
     * POST /contact/:id/communication — hängt eine E-Mail an den Kontakt.
     * Serverseitig dedupliziert (createCommunication) und RBAC-geprüft
     * (@Rels('contacts','update') + assertContactWritable).
     */
    async attachEmail(
        contactId: number,
        payload: KommunikationPayload,
    ): Promise<void> {
        return contactCommunicationService.attachCommunication(contactId, payload);
    },

    /**
     * Cluster ZUWEISEN (C9) — derselbe Endpoint für JEDEN Kommunikationstyp
     * (Telefon, Mobil, Fax, E-Mail). `attachEmail` ist nur noch der alte Name
     * dafür; Serverseite und Fehlerbehandlung sind identisch.
     *
     * `kind`/`companyId` sind an Nicht-Personen verboten — der Aufrufer lässt
     * sie dort weg, sonst antwortet der Server mit 400
     * (`assertCommunicationKindCompany`).
     */
    async attachCommunication(contactId: number, payload: KommunikationPayload): Promise<void> {
        const res = await fetch(apiUrl(`contact/${contactId}/communication`), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });
        if (!res.ok) {
            const txt = await res.text();
            throw new Error(`Kontaktdaten hinterlegen fehlgeschlagen (${res.status}): ${txt}`);
        }
    },
};
